import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../lib/api';
import { useMyTenant } from '../hooks/useMyTenant';
import { getOnboardingPathForTier } from '../lib/tier';
import { safeDriveUrl } from '../lib/driveUrl';

// ── Types ─────────────────────────────────────────────────────────────────────

interface BootstrapAction {
  id:           string;
  actionType:   string;
  result:       string;
  resultDetail: string | null;
  executedAt:   string | null;
}

interface BootstrapResource {
  id:           string;
  resourceType: string;
  resourceId:   string | null;
  status:       string;
  metadata:     Record<string, unknown> | null;
}

interface BootstrapSummary {
  total:     number;
  succeeded: number;
  failed:    number;
  skipped:   number;
  pending:   number;
}

interface BootstrapStatus {
  projectId: string;
  actions:   BootstrapAction[];
  resources: BootstrapResource[];
  summary:   BootstrapSummary;
}

interface InfrastructureCardProps {
  projectId: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const RESOURCE_LABELS: Record<string, string> = {
  GITHUB_REPO:     'GitHub Repository',
  DRIVE_FOLDER:    'Drive Folder',
  SHAREPOINT_SITE: 'SharePoint Site',
  CI_PIPELINE:     'CI Pipeline',
};

const POLL_INTERVAL_MS = 3000;
// FT-PROVISION — bound the "no actions yet" wait. provisionProject writes its
// first bootstrap_actions row within seconds of dispatch (even the no-infra-
// config path writes a CHECK_INFRA_CONFIG row immediately). If after this many
// empty polls (~60s) there are still zero actions, provisioning was never
// dispatched for this project — render an honest "not started" state and stop
// polling, rather than spinning "Provisioning in progress…" forever (the
// Fast-Track stuck symptom).
const MAX_EMPTY_POLLS = 20;

// ── Helpers ───────────────────────────────────────────────────────────────────

function isTerminal(status: BootstrapStatus): boolean {
  if (status.actions.length === 0) return false;
  // Terminal when no resources are PENDING
  const hasPending = status.resources.some(r => r.status === 'PENDING');
  if (hasPending) return false;
  // Also check for SKIPPED-only (no infra config case)
  if (status.actions.every(a => a.result === 'SKIPPED')) return true;
  return status.resources.length > 0 && !hasPending;
}

function isSkippedState(status: BootstrapStatus): boolean {
  return (
    status.actions.length > 0 &&
    status.actions.every(a => a.result === 'SKIPPED') &&
    status.resources.length === 0
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function InfrastructureCard({ projectId }: InfrastructureCardProps) {
  const [status, setStatus]     = useState<BootstrapStatus | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [notStarted, setNotStarted] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const emptyPollsRef = useRef(0);
  const { myTenant } = useMyTenant();
  const onboardingHref = getOnboardingPathForTier(myTenant?.subscriptionTier);

  const fetchStatus = async () => {
    try {
      const data = await apiFetch<BootstrapStatus>(`/projects/${projectId}/bootstrap/status`);
      setStatus(data);
      return data;
    } catch {
      return null;
    }
  };

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  // Decide whether to stop polling for a freshly-fetched status (true = stop).
  // A status with zero actions is the "provisioning hasn't started" window:
  // keep polling (provisionProject is dispatched async and writes its first
  // action within seconds) UNTIL we've waited MAX_EMPTY_POLLS, after which we
  // conclude it was never dispatched and surface an honest "not started" state.
  // Any actions clear that conclusion and revert to the normal terminal check.
  const evaluate = (data: BootstrapStatus | null): boolean => {
    if (!data) return false; // transient fetch error — keep trying
    if (data.actions.length === 0) {
      emptyPollsRef.current += 1;
      if (emptyPollsRef.current >= MAX_EMPTY_POLLS) {
        setNotStarted(true);
        return true;
      }
      return false;
    }
    emptyPollsRef.current = 0;
    setNotStarted(false);
    return isTerminal(data);
  };

  const beginPolling = () => {
    stopPolling();
    pollRef.current = setInterval(() => {
      void fetchStatus().then(latest => {
        if (evaluate(latest)) stopPolling();
      });
    }, POLL_INTERVAL_MS);
  };

  useEffect(() => {
    emptyPollsRef.current = 0;
    setNotStarted(false);
    void fetchStatus().then(data => {
      if (!evaluate(data)) beginPolling();
    });
    return () => stopPolling();
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await apiFetch(`/projects/${projectId}/bootstrap`, {
        method: 'POST',
        body:   JSON.stringify({}),
      });
      // Resume polling after retry — reset the empty-poll budget so a retry
      // that re-dispatches provisioning gets a fresh wait window. Stop any
      // in-flight poll up front so a terminal/not-started result leaves no
      // orphaned interval running.
      stopPolling();
      emptyPollsRef.current = 0;
      setNotStarted(false);
      const data = await fetchStatus();
      if (!evaluate(data)) beginPolling();
    } catch { /* silent */ } finally {
      setRetrying(false);
    }
  };

  // ── Render states ───────────────────────────────────────────────────────────

  // Not yet loaded — first fetch in flight (or a transient fetch error)
  if (!status) {
    return (
      <CardShell>
        <Header label="Infrastructure" badge={null} />
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
          Checking infrastructure…
        </p>
      </CardShell>
    );
  }

  // No actions at all. Two cases:
  //   • notStarted=false → still inside the spin-up window (provisioning was
  //     just dispatched; its first action lands within seconds) — show progress.
  //   • notStarted=true  → MAX_EMPTY_POLLS elapsed with zero actions, so
  //     provisioning was never dispatched for this project. Tell the truth and
  //     stop implying work is underway. (The Development gate still needs a repo;
  //     the GitHubSetupBanner's "Configure GitHub" remains the path to set one.)
  if (status.actions.length === 0) {
    return (
      <CardShell>
        <Header
          label="Infrastructure"
          badge={notStarted ? { label: 'Not started', color: 'var(--text-secondary)' } : null}
        />
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
          {notStarted
            ? 'Infrastructure provisioning hasn’t started for this project.'
            : '⏳ Provisioning in progress…'}
        </p>
      </CardShell>
    );
  }

  // Skipped — no BYOI config
  if (isSkippedState(status)) {
    return (
      <CardShell>
        <Header label="Infrastructure" badge={{ label: 'Not configured', color: 'var(--text-secondary)' }} />
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
          ℹ Complete organisation setup to enable automatic provisioning.
        </p>
        <a
          href={onboardingHref}
          style={{
            display: 'inline-block',
            fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
            color: 'var(--primary)', textDecoration: 'none',
            padding: '6px 14px',
            border: '1px solid rgba(124,58,237,0.3)',
            borderRadius: 'var(--radius-sm)',
          }}
        >
          Complete Setup →
        </a>
      </CardShell>
    );
  }

  // Has resources — show list
  const allProvisioned = status.resources.length > 0 &&
    status.resources.every(r => r.status === 'PROVISIONED');
  const hasFailed      = status.resources.some(r => r.status === 'FAILED');
  const hasPending     = status.resources.some(r => r.status === 'PENDING');

  const badge = hasPending
    ? { label: 'In progress…', color: 'var(--warning)' }
    : allProvisioned
      ? { label: '✓ Ready',    color: 'var(--success)' }
      : hasFailed
        ? { label: '⚠ Partial', color: 'var(--warning)' }
        : null;

  return (
    <CardShell>
      <Header label="Infrastructure" badge={badge} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {status.resources.map(resource => (
          <ResourceRow
            key={resource.id}
            resource={resource}
            projectId={projectId}
            onRetry={hasFailed ? handleRetry : undefined}
            retrying={retrying}
          />
        ))}
        {hasPending && (
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
            ⏳ Provisioning in progress…
          </p>
        )}
      </div>
    </CardShell>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function CardShell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      padding: '16px 20px',
      borderRadius: 'var(--radius-sm)',
      border: '1px solid var(--border)',
      background: 'var(--surface)',
    }}>
      {children}
    </div>
  );
}

function Header({ label, badge }: { label: string; badge: { label: string; color: string } | null }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
      <span style={labelStyle}>{label}</span>
      {badge && (
        <span style={{
          fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
          color: badge.color,
        }}>
          {badge.label}
        </span>
      )}
    </div>
  );
}

// DRV-5b(i) (#2883) — the Drive folder row no longer carries an id
// (bootstrap/status omits it). The link comes from the members-only folder
// endpoint and is shown only when lib/driveUrl.ts accepts it.
function DriveFolderLink({ projectId }: { projectId: string }) {
  const [href, setHref] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    apiFetch<{ status: string; webUrl?: string }>(`/projects/${projectId}/storage/folder`)
      .then(data => { if (!cancelled) setHref(safeDriveUrl(data.webUrl)); })
      .catch(() => { if (!cancelled) setHref(null); });
    return () => { cancelled = true; };
  }, [projectId]);
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--primary)', flexShrink: 0 }}
    >
      Drive folder ↗
    </a>
  );
}

function ResourceRow({
  resource,
  projectId,
  onRetry,
  retrying,
}: {
  resource:  BootstrapResource;
  projectId: string;
  onRetry?:  () => void;
  retrying:  boolean;
}) {
  const icon  = resource.status === 'PROVISIONED' ? '✓'
               : resource.status === 'FAILED'      ? '✕'
               : resource.status === 'SKIPPED'     ? '↷'
               : '⏳';
  const color = resource.status === 'PROVISIONED' ? 'var(--success)'
               : resource.status === 'FAILED'      ? 'var(--error)'
               : 'var(--text-secondary)';
  const label = RESOURCE_LABELS[resource.resourceType] ?? resource.resourceType;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ color, fontSize: 13, fontFamily: 'var(--font-body)', flexShrink: 0 }}>{icon}</span>
      <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', flex: 1, minWidth: 0 }}>
        {label}
      </span>
      {resource.status === 'PROVISIONED' && resource.resourceType === 'DRIVE_FOLDER' && (
        <>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', flexShrink: 0 }}>
            Provisioned
          </span>
          <DriveFolderLink projectId={projectId} />
        </>
      )}
      {resource.status === 'PROVISIONED' && resource.resourceType !== 'DRIVE_FOLDER' && resource.resourceId && (
        <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', flexShrink: 0 }}>
          {resource.resourceId.slice(0, 24)}…
        </span>
      )}
      {resource.status === 'FAILED' && (
        <>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)', flexShrink: 0, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {resource.metadata?.error as string ?? 'provisioning failed'}
          </span>
          {onRetry && (
            <button
              onClick={onRetry}
              disabled={retrying}
              style={{
                flexShrink: 0,
                fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
                padding: '3px 10px',
                background: 'transparent',
                border: '1px solid rgba(239,68,68,0.4)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--error)',
                cursor: retrying ? 'not-allowed' : 'pointer',
                opacity: retrying ? 0.6 : 1,
              }}
            >
              {retrying ? '…' : 'Retry'}
            </button>
          )}
        </>
      )}
    </div>
  );
}
